import { Component, ComponentInterface, JSX, Prop, State, h } from '@stencil/core';
import { ButtonType } from '@aglenergy/design-system/dist/types/components/core/button/button.types';
import { StripColorType } from '@aglenergy/design-system-components/dist/types/components/card/static-card/card';
import { store } from 'app/shared/store/store';
import { featureService, productService } from 'app/shared/services/index';
import { AddressDetailModel, FeatureKey, ProductCatalogueContentFeature, ProductCatalogueProduct } from 'app/shared/models/index';
import { StoreKeys } from 'app/shared/constants/index';
import { changePlanService } from 'app/nbn/services/change-plan/change-plan.service';
import { CommonLinks } from 'app/shared/components/common-links/common-links';
import { mergeProductDetailsWithNbnCommonLinks, renderContentBelowCards } from 'app/shared/utilities/helper-functions';

@Component({
  tag: 'nbn-change-plan-product-card',
  styleUrl: 'nbn-change-plan-product-card.scss'
})
export class NbnChangePlanProductCard implements ComponentInterface {
  @Prop() public focusCardIndex: number;
  @Prop() public isPersonalisedProductsLoading: boolean;
  @Prop() public products: ProductCatalogueProduct[];
  @Prop() public selectedAddress: AddressDetailModel;
  @Prop() public productCategories?: string[];

  @State() private hasValidEnergyContract = false;

  private cardIdPrefix = 'card';

  public async componentWillLoad(): Promise<void> {
    this.hasValidEnergyContract = await changePlanService.hasEnergyContract();
  }

  public componentDidRender(): void {
    this.scrollToTop();
  }

  private getFeatureIcon(featureIconName?: string): JSX.Element {
    if (!featureIconName) {
      return null;
    }
    if (featureIconName.includes('.')) {
      return <dls-dynamic-icon iconPath={featureIconName} altText="" />;
    }
    // TODO: remove decorative icon selection after cleanup
    const iconNameInitial = featureIconName.charAt(0);
    return iconNameInitial >= 'a' && iconNameInitial <= 'l' ? (
      <dls-decorative-icon-set1 id="svg" size="sm" type={featureIconName as any} />
    ) : (
      <dls-decorative-icon-set2 id="svg" size="sm" type={featureIconName as any} />
    );
  }

  public onChangeButtonClick(product: ProductCatalogueProduct): void {
    !this.isPersonalisedProductsLoading &&
      productService.onSelectedProductForNbn(this.products, this.selectedAddress, product.sku, 'productswap');
  }

  public onSeePlansButtonClick(): void {
    changePlanService.clearSelectedProduct();
  }

  private scrollToTop(): void {
    const comparisonContainer = document.getElementsByClassName('current-plan-info');
    if (comparisonContainer.length > 0) comparisonContainer[0].scrollIntoView();
  }

  public render(): JSX.Element {
    let anyStripText = this.products?.some((product) => product.content?.stripText);
    const currentPlanSku = changePlanService.getCurrentPlanSku();
    const selectedProductSku = changePlanService.getSelectedProductSku();
    let selectedProductPlan: ProductCatalogueProduct | null = null;
    if (selectedProductSku && currentPlanSku !== selectedProductSku) {
      selectedProductPlan = this.products?.find((prod) => prod.sku === selectedProductSku);
    }
    const showNbnFaq = this.productCategories?.length == 2 && store.get('selectedNbnProductDetails') === null;
    const selectedPlanCategory = store.get(StoreKeys.selectedPlanCategory);
    const techType = (store.get(StoreKeys.productDetails) as any)?.serviceQualification?.nbn?.serviceType;
    const productCategory = store.get(StoreKeys.productCategory);
    let generalContentCategoryItems = store.get(StoreKeys.generalContent).categoryItems;
    const nbnCommonLinks = featureService.getFeatureStatus(FeatureKey.NBN_COMMON_LINKS_ENABLED)
      ? store.get(StoreKeys.nbnCommonLinks)
      : null;
    if (nbnCommonLinks) {
      generalContentCategoryItems = mergeProductDetailsWithNbnCommonLinks(nbnCommonLinks);
    }
    document.documentElement.style.setProperty('--scrollbar-width', `${window.innerWidth - document.documentElement.clientWidth}px`);
    return (
      <div>
        {selectedProductPlan && (
          <div class="selected-plan-title-container">
            <dls-h2 class="selected-plan-title">{`You're changing to ${selectedProductPlan.name}`}</dls-h2>
            <dls-p class="selected-plan-subtitle">
              <strong>Did you see a different price before?</strong> Some offers are for new <strong>nbn</strong> connections only.
            </dls-p>
          </div>
        )}
        <div class="change-plan-container" id="comparison-container">
          {this.products && (
            <div>
              <carousel-buttons numberOfProducts={this.products.length} focusedCardIndex={this.focusCardIndex} showPagination={true}>
                <div class="card-container" id="card-container">
                  {this.products.map((product, index) => {
                    const isCurrentPlan = currentPlanSku === product.sku;
                    anyStripText = anyStripText || isCurrentPlan;
                    const buttonLabel = isCurrentPlan ? 'Remove plan' : 'Change to this plan';
                    const buttonType = isCurrentPlan ? 'secondary' : 'primary';
                    const hasSelectedPlan = selectedProductSku !== null && currentPlanSku !== selectedProductSku;
                    return this.nbnCardRender(isCurrentPlan, anyStripText, hasSelectedPlan, product, index, buttonType, buttonLabel);
                  })}
                </div>
              </carousel-buttons>
              {selectedProductSku && selectedProductSku !== currentPlanSku && this.ctaButtonsRender()}
            </div>
          )}
        </div>
        {generalContentCategoryItems !== undefined && generalContentCategoryItems.length && (
          <CommonLinks
            generalContentCategoryItems={generalContentCategoryItems}
            productCategory={productCategory}
            selectedPlanCategory={selectedPlanCategory}
            techType={techType}
          />
        )}
        <div class="below-cards-message">
          <message-wrapper
            message={`<p>You can change your plan once a month. If you upgrade your plan before the end of your monthly billing cycle, you'll just need to pay the difference between your current plan and new plan. if you move to lower speed plan, you wont receive a pro rata refund for fees paid in advance.</p>`}
          />
          {renderContentBelowCards(this.products)}
        </div>
        {showNbnFaq && <nbn-faq />}
      </div>
    );
  }

  private nbnCardRender(
    isCurrentPlan: boolean,
    anyStripText: boolean,
    hasSelectedPlan: boolean,
    product: ProductCatalogueProduct,
    index: number,
    buttonType: ButtonType,
    buttonLabel: string
  ): any {
    const displayToUser = product.qualified !== false;
    const productHeadline = product.content
      ? product.content?.productHeadline.replace(/nbn/gi, '<strong>nbn</strong>')
      : `<strong>nbn</strong> ${product.attributes['download_speed']}/${product.attributes['upload_speed']}`;
    let stripText = product.content?.stripText;
    let stripColor: StripColorType = anyStripText ? 'secondary-04' : 'none';
    if (isCurrentPlan) {
      stripText = 'Current plan';
      stripColor = 'none';
    }
    let bundlePriceDesc = 'when you BYO modem and have AGL energy.';
    const hasHoneymoonOffer = product.content ? product.content.bundleOfferPrice !== product.attributes.agl_marketing_price : false;
    if (product.content && displayToUser) {
      if (!hasHoneymoonOffer) {
        bundlePriceDesc = product.content.bundleOfferText || bundlePriceDesc;
      } else if (product.fibreUpgrade) {
        bundlePriceDesc = product.content.bundleOfferText || bundlePriceDesc;
      }
    }

    return (
      <div class="card__wrapper">
        <div class={`card scroll-snap ${!displayToUser ? 'unavailable' : ''}`} id={`${this.cardIdPrefix}${index}`}>
          {/*
            // https://aglenergy.atlassian.net/browse/TEC-26252: changes to remove the current plan
            // from IPP page as per design due to incorrect pricing. Commented following code
            // until future notice.
           isSelectedPlan && (
            <div class="card__plan-selected__strip-icon">
              <agl-ds-icon icon-path="/apps/comparison/assets/icon/agl_tile_tick.svg" size="xs" />
            </div>
          )*/}
          <dls-card
            padding="md"
            elevationType="level1"
            radius="round02"
            stripColor={stripText ? stripColor : 'none'}
            stripText={stripText}
            topMargin={hasSelectedPlan ? 'space03' : 'space06'}
          >
            <div>
              <dls-h3 styledAs="title4">{product.content ? product.content?.productName : product.name}</dls-h3>
              <div class="product-headline" innerHTML={productHeadline} />
            </div>
            <div class="unavailable-overlay-container">
              <div class={!displayToUser && 'unavailable-overlay'} hidden={displayToUser}>
                <info-tag heading={product.content?.unavailableHeading} content={product.content?.unavailableContent} />
              </div>
              <div>
                <telco-new-price-tag
                  price={product.fibreUpgrade ? product.content?.bundleOfferPrice : product.attributes.agl_marketing_price}
                  paymentFreq={product.content ? product.content?.priceVariable : 'a month'}
                  priceDesc={bundlePriceDesc}
                  bundleTitle={product.content ? product.content?.bundleOfferStrip : 'AGL Bundle Offer'}
                />
                {!this.hasValidEnergyContract && (
                  <telco-new-price-tag
                    price={product.content?.withoutOfferPrice || product.price}
                    paymentFreq={product.content ? product.content?.priceVariable : 'a month'}
                    priceDesc={product.content ? product.content?.withoutOfferText : ''}
                  />
                )}
              </div>
              {product.content && product.content.features ? (
                <div class="feature-list">
                  {product.content?.features.map((feature: ProductCatalogueContentFeature) => {
                    return (
                      <telco-feature-text desc={feature.featureDescription}>{this.getFeatureIcon(feature.featureIcon)}</telco-feature-text>
                    );
                  })}
                </div>
              ) : (
                <div class="feature-list-empty">Sorry we can't show this plans features right now</div>
              )}
              {!isCurrentPlan && !hasSelectedPlan ? (
                <div class="cta--button">
                  <dls-button
                    variant={buttonType}
                    loading={this.isPersonalisedProductsLoading || store.get('skuBeingRemoved') === product.sku}
                    data-testid="cta-main"
                    // eslint-disable-next-line react/jsx-no-bind
                    onClick={() => displayToUser && this.onChangeButtonClick(product)}
                  >
                    {this.selectedAddress ? buttonLabel : 'Check your address'}
                  </dls-button>
                </div>
              ) : (
                <div class={!hasSelectedPlan ? 'cta--empty-button-container' : ''} />
              )}
              <div class="links">
                {product.content?.links?.map((link) => {
                  if (isCurrentPlan || (hasHoneymoonOffer && !product.fibreUpgrade && link.title.toLowerCase() === 'agl offer terms'))
                    return <div />;
                  return (
                    <div>
                      <dls-link href={link.url} open-in-new-window>
                        {link.title}
                      </dls-link>
                    </div>
                  );
                })}
              </div>
            </div>
          </dls-card>
        </div>
      </div>
    );
  }

  private ctaButtonsRender() {
    const product = this.products.find((p) => p.sku === changePlanService.getSelectedProductSku());
    return (
      <div class="cta--container">
        <dls-button
          variant={'primary'}
          loading={this.isPersonalisedProductsLoading}
          data-testid="cta-main"
          // eslint-disable-next-line react/jsx-no-bind
          onClick={() => this.onChangeButtonClick(product)}
        >
          {'Change to this plan'}
        </dls-button>
        <dls-button
          variant={'tertiary'}
          loading={this.isPersonalisedProductsLoading}
          data-testid="cta-secondary"
          // eslint-disable-next-line react/jsx-no-bind
          onClick={this.onSeePlansButtonClick}
        >
          {'See other nbn plans'}
        </dls-button>
      </div>
    );
  }
}
